import type { Span } from "@agentdash/shared";

/**
 * Depth of each span in the parent chain, keyed by `span_id`, for indenting the
 * trace tree.
 *
 * `seen` tracks the current recursion path so a cyclic or self-referencing
 * parent_span_id (corrupt OTLP) terminates the recursion instead of
 * overflowing the stack and crashing the whole detail view. That bound is
 * the guarantee; the resulting depths are not zero. Re-entering a span
 * already on the path caches it at 0 and returns, but that write is
 * overwritten as the recursion unwinds — for a two-node cycle
 * (A.parent=B, B.parent=A) walked from A the map ends up {A: 2, B: 1}, and a
 * self-referencing span ends at 1 rather than 0. So a
 * cycle yields arbitrary-but-bounded depths that depend on span iteration
 * order, which only shifts an indent and is why it is left as is.
 */
export function buildIndentMap(spans: Span[]): Map<string, number> {
  const byId = new Map(spans.map((s) => [s.span_id, s]));
  const depthCache = new Map<string, number>();
  function depthOf(s: Span, seen: Set<string>): number {
    const cached = depthCache.get(s.span_id);
    if (cached !== undefined) return cached;
    if (!s.parent_span_id || seen.has(s.span_id)) {
      depthCache.set(s.span_id, 0);
      return 0;
    }
    const parent = byId.get(s.parent_span_id);
    seen.add(s.span_id);
    const d = parent ? depthOf(parent, seen) + 1 : 1;
    depthCache.set(s.span_id, d);
    return d;
  }
  const map = new Map<string, number>();
  for (const s of spans) map.set(s.span_id, depthOf(s, new Set()));
  return map;
}
